import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ArrowOutward,
  ExpandMore,
  Sync,
  ShoppingBag,
  Storefront,
  WhatsApp,
  FactoryOutlined,
  AutoAwesome,
  GroupsOutlined,
  InsightsOutlined,
  WarehouseOutlined,
  NorthEast,
  FiberManualRecord,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import PartnersStrip from '../components/public/PartnersStrip';
import { whatsappLink } from '../components/public/publicTokens';
import { trackFunnel } from '../utils/tracking';

/* =============================================================================
 * DESIGN TOKENS
 * ---------------------------------------------------------------------------
 * Editorial + warehouse-terminal aesthetic. The page is intentionally decoupled
 * from the in-app MUI theme (slate + indigo app) so the landing has its own
 * voice: warm cream paper, ink black, a loud yellow used as an inventory alert.
 * ========================================================================== */
const CREAM = '#F5EFE0';
const CREAM_SOFT = '#FAF6EC';
const INK = '#0A0A0A';
const INK_SOFT = '#1C1B1A';
const YELLOW = '#F6D058';
const YELLOW_DEEP = '#E8B923';
const INDIGO = '#1e40af';
const RULE = 'rgba(10,10,10,0.12)';
const RULE_HARD = 'rgba(10,10,10,0.22)';

/* ── Atmósfera del hero ──────────────────────────────────────────────────────
 * El hero era crema plano: cero profundidad. Se le pone una malla de gradiente
 * difuminada + grano de papel, ambos generados (sin assets, sin red, sin CSP).
 * Si algún día hay un mp4 de fondo, se setea HERO_VIDEO y el video se dibuja
 * encima de la malla, siempre debajo del velo crema que garantiza la lectura. */
// Card #22 (02/11/2026): video cinemático de fondo, estilo rutazo.com. Loop de 7 s,
// 720p, sin audio (~0,9 MB), generado con Seedance 2.0. Se ve nítido detrás de un velo
// crema horizontal: fuerte del lado del titular, suave del lado de la tarjeta.
// Con prefers-reduced-motion queda solo el póster. Nombres versionados (-v1) para que
// ningún caché sirva el clip difuminado anterior.
const HERO_VIDEO = '/media/hero-cine-v1.mp4';
const HERO_POSTER = '/media/hero-cine-v1-poster.jpg';

/** Grano analógico: SVG inline via feTurbulence, ~0 KB y sin pedido de red. */
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='240' height='240' filter='url(%23n)' opacity='0.38'/%3E%3C/svg%3E\")";

const DISPLAY = '"Instrument Serif", "Times New Roman", Georgia, serif';
const SANS = '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

/* =============================================================================
 * DATA
 * ========================================================================== */
const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Producto', href: '#features' },
  { label: 'Cómo funciona', href: '#how' },
  { label: 'Precio', href: '#pricing' },
  { label: 'Preguntas', href: '#faq' },
];

const FEATURES = [
  {
    n: '01',
    tag: 'Sincronización',
    title: 'Un stock, todos los canales.',
    body:
      'Cuando alguien compra en MercadoLibre, el stock se descuenta en Tienda Nube y en tu punto de venta al instante. Ni un segundo de desfasaje — ni una reventa fantasma. Y si querés consultar stock o precios desde el celular, el agente IA de WhatsApp te responde por chat (Plan IA).',
    bullets: ['MercadoLibre', 'Tienda Nube', 'Punto de venta (mostrador)'],
    icon: <Sync sx={{ fontSize: 22 }} />,
  },
  {
    n: '02',
    tag: 'Producción',
    title: 'Recetas, BOM, órdenes de fabricación.',
    body:
      'Definí qué insumos entran en cada producto. Al cerrar una orden, el sistema descuenta materias primas y suma el terminado. Sin planillas paralelas.',
    bullets: ['Recetas con variantes', 'Listas de materiales', 'Órdenes con rendimiento'],
    icon: <FactoryOutlined sx={{ fontSize: 22 }} />,
  },
  {
    n: '03',
    tag: 'Reposición',
    title: 'Compras sugeridas por velocidad real.',
    body:
      'No más "¿esto se vende?". DashFlow mira tu histórico de ventas, controla el punto de reposición y te sugiere qué comprar antes de que un SKU entre en rojo.',
    bullets: ['Punto de reposición por producto', 'Alertas tempranas', 'Sugerencias de compra armadas'],
    icon: <AutoAwesome sx={{ fontSize: 22 }} />,
  },
  {
    n: '04',
    tag: 'CRM',
    title: 'Oportunidades, clientes, actividades.',
    body:
      'Dejá de saltar entre Excel, WhatsApp y mails. Tus vendedores trabajan en un pipeline conectado al mismo stock que ven ventas y producción.',
    bullets: ['Pipeline con etapas', 'Actividades por vendedor', 'Histórico unificado'],
    icon: <GroupsOutlined sx={{ fontSize: 22 }} />,
  },
  {
    n: '05',
    tag: 'Analytics',
    title: 'Plata. Margen. Rotación.',
    body:
      'Dashboards que te dicen qué vender, qué rematar, qué fabricar. Cash flow, margen por canal, ABC de productos — con datos reales, no con planillas.',
    bullets: ['Margen real por SKU', 'Ventas por canal', 'Cash flow proyectado'],
    icon: <InsightsOutlined sx={{ fontSize: 22 }} />,
  },
  {
    n: '06',
    tag: 'Multi-sede',
    title: 'Un negocio, varios depósitos.',
    body:
      'Separá stock por sucursal, depósito o canal. Transferencias internas, trazabilidad por lote y visibilidad total desde una misma cuenta.',
    bullets: ['Depósitos ilimitados', 'Lotes y vencimientos', 'Transferencias entre depósitos'],
    icon: <WarehouseOutlined sx={{ fontSize: 22 }} />,
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Conectá los canales',
    body: 'Linkeás MercadoLibre y Tienda Nube con OAuth. Sin desarrollo, sin consultores.',
    time: '≤ 4 min',
  },
  {
    n: '02',
    title: 'Importá el catálogo',
    body: 'Subís tu Excel o CSV. Nosotros matcheamos cada SKU con la publicación de cada canal.',
    time: '≤ 20 min',
  },
  {
    n: '03',
    title: 'Vendé sin pensarlo',
    body: 'Cada venta descuenta stock en todos los canales. Producción, ventas y depósito ven lo mismo.',
    time: 'todo el día',
  },
];

const FAQS = [
  {
    q: '¿Cuánto tardo en tenerlo andando?',
    a: 'La mayoría arranca el mismo día. La conexión con MercadoLibre y Tienda Nube se hace con OAuth (el estándar que usan también las apps del propio canal) y te acompañamos en la importación del catálogo con plantillas de Excel.',
  },
  {
    q: '¿Puedo probarlo antes de pagar?',
    a: 'Sí. Toda cuenta arranca en el plan Gratis, para siempre y sin tarjeta: ventas, stock y hasta 100 productos con 1 usuario. Cuando necesites más productos, usuarios o módulos, te pasás a un plan pago.',
  },
  {
    q: '¿Qué pasa si ya uso un ERP o Tango?',
    a: 'DashFlow convive con tu ERP. Podemos importar desde Excel o CSV (incluyendo exportaciones de Tango o Bejerman). Muchos clientes nos usan como capa de stock + ventas + canales, y dejan la contabilidad donde ya la tenían.',
  },
  {
    q: '¿Soporta fabricación con recetas?',
    a: 'Sí. Definís recetas con insumos, materias primas y servicios, creás órdenes de producción y el sistema descuenta automáticamente lo consumido cuando cerrás la orden. Con rendimiento, scrap y lote.',
  },
  {
    q: '¿Cómo funciona el multi-tenant?',
    a: 'Cada empresa opera en su propio subdominio aislado (ej. tu-empresa.dashflow.com.ar). Los datos nunca se cruzan entre cuentas y vos administrás tus propios usuarios y permisos.',
  },
  {
    q: '¿Hay soporte en español?',
    a: 'Todo el equipo está en Argentina. Soporte por chat, mail y reuniones. Sin call-centers ni tickets en inglés.',
  },
];

/* Planes reales en ARS — espejo de GET /api/subscription-plans (mismos datos
 * que PricingPage.tsx). Se usan como fallback si la API no responde. */
interface LandingPlan {
  code: string;
  name: string;
  price: number;
  blurb: string;
  bullets: string[];
  highlight?: boolean;
}

const PLANS: LandingPlan[] = [
  {
    code: 'free',
    name: 'Gratis',
    price: 0,
    blurb: 'Para empezar a vender y controlar tu stock, sin pagar nada.',
    bullets: [
      'Ventas con lector de código de barras',
      'Gestión de stock',
      'Hasta 100 productos · 1 usuario',
      'Gratis para siempre, sin tarjeta',
    ],
  },
  {
    code: 'esencial',
    name: 'Esencial',
    price: 60000,
    blurb: 'Lo necesario para vender y controlar tu stock.',
    bullets: [
      'Ventas con lector de código de barras',
      'Gestión de stock y reposición',
      'Clientes y cuentas corrientes',
      'Hasta 3.500 productos · 2 usuarios',
      'Facturación de ARCA (costo adicional)',
    ],
    highlight: true,
  },
  {
    code: 'pro',
    name: 'Pro',
    price: 100000,
    blurb: 'Más capacidad y soporte para balanzas.',
    bullets: [
      'Todas las funciones del Plan Esencial',
      'Hasta 10.000 productos · 5 usuarios',
      'Soporte para balanzas',
      'Facturación de ARCA (costo adicional)',
    ],
  },
  {
    code: 'ia',
    name: 'IA',
    price: 150000,
    blurb: 'Inteligencia para tu negocio.',
    bullets: [
      'Todas las funciones del Plan Pro',
      'Escaneo inteligente de facturas',
      'Agente IA de WhatsApp: consultá stock y precios por chat',
      'Detecta productos que no se venden',
      'Facturación de ARCA (costo adicional)',
    ],
  },
];

const fmtARS = (n: number) => n.toLocaleString('es-AR');

const TICKER = [
  'MELI · ONLINE',
  'STOCK SYNC · 0.3 S',
  'NUBE · SYNCED',
  'ARCA · FACTURA OK',
  'OPS · 24/7',
  'ARGENTINA · ARS',
  'MELI · ONLINE',
  'REPO · SUGERENCIAS ON',
  'NUBE · SYNCED',
  'PROD · 3 ÓRDENES',
];

/* =============================================================================
 * HELPERS
 * ========================================================================== */

/** Scroll reveal — subtle, runs once when element enters viewport. */
const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  y?: number;
  as?: React.ElementType;
}> = ({ children, delay = 0, y = 24, as = 'div' }) => {
  const MotionComponent = motion(as as any);
  return (
    <MotionComponent
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    >
      {children}
    </MotionComponent>
  );
};

/** Section wrapper with consistent vertical rhythm and an optional full-bleed bg. */
const Section: React.FC<{
  id?: string;
  bg?: string;
  color?: string;
  children: React.ReactNode;
  sx?: object;
}> = ({ id, bg, color, children, sx = {} }) => (
  <Box
    component="section"
    id={id}
    sx={{
      position: 'relative',
      bgcolor: bg,
      color,
      py: { xs: 10, md: 14 },
      scrollMarginTop: 80,
      ...sx,
    }}
  >
    <Container maxWidth="lg">{children}</Container>
  </Box>
);

/** Editorial section label — small caps + serifed index number. */
const SectionLabel: React.FC<{ idx: string; children: React.ReactNode; color?: string }> = ({
  idx,
  children,
  color,
}) => (
  <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 3 }}>
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: '0.72rem',
        letterSpacing: '0.18em',
        color: color ?? INK,
        opacity: 0.6,
      }}
    >
      {idx}
    </Typography>
    <Box sx={{ flex: 1, height: 1, bgcolor: color ? 'rgba(255,255,255,0.25)' : RULE }} />
    <Typography
      sx={{
        fontFamily: SANS,
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: color ?? INK,
        opacity: 0.8,
      }}
    >
      {children}
    </Typography>
  </Stack>
);

/** Pill-style outlined button — refined, not chunky. */
const pillBtn = (variant: 'solid' | 'outline' | 'ghost' = 'solid', on: 'light' | 'dark' = 'light') => {
  const isDark = on === 'dark';
  if (variant === 'solid') {
    return {
      bgcolor: INK,
      color: CREAM_SOFT,
      borderRadius: 999,
      px: 3,
      py: 1.4,
      fontFamily: SANS,
      fontSize: '0.88rem',
      fontWeight: 600,
      letterSpacing: '0.01em',
      textTransform: 'none' as const,
      border: '1px solid',
      borderColor: INK,
      boxShadow: 'none',
      '&:hover': {
        bgcolor: INDIGO,
        borderColor: INDIGO,
        boxShadow: 'none',
      },
    };
  }
  if (variant === 'outline') {
    return {
      bgcolor: 'transparent',
      color: isDark ? CREAM_SOFT : INK,
      borderRadius: 999,
      px: 3,
      py: 1.4,
      fontFamily: SANS,
      fontSize: '0.88rem',
      fontWeight: 600,
      textTransform: 'none' as const,
      border: '1px solid',
      borderColor: isDark ? 'rgba(255,255,255,0.3)' : RULE_HARD,
      '&:hover': {
        bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(10,10,10,0.04)',
        borderColor: isDark ? CREAM_SOFT : INK,
      },
    };
  }
  return {
    bgcolor: 'transparent',
    color: isDark ? CREAM_SOFT : INK,
    borderRadius: 999,
    px: 2,
    py: 1,
    fontFamily: SANS,
    fontSize: '0.88rem',
    fontWeight: 500,
    textTransform: 'none' as const,
    '&:hover': { bgcolor: 'transparent', opacity: 0.7 },
  };
};

/* =============================================================================
 * NAV — masthead style, hairline border
 * ========================================================================== */
const Nav: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          bgcolor: CREAM,
          borderBottom: `1px solid ${RULE}`,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" sx={{ py: 2.2 }} spacing={2}>
            <Box component={RouterLink} to="/" sx={{ textDecoration: 'none', color: INK, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.55rem', lineHeight: 1, fontWeight: 400 }}>DashFlow</Typography>
              <Typography sx={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: '1.55rem', lineHeight: 1, color: YELLOW_DEEP }}>Pro</Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            {!isMobile && (
              <Stack direction="row" spacing={3.5}>
                {NAV_LINKS.map((l) => (
                  <Box
                    key={l.href}
                    component="a"
                    href={l.href}
                    sx={{
                      fontFamily: SANS,
                      fontSize: '0.9rem',
                      color: INK,
                      textDecoration: 'none',
                      position: 'relative',
                      '&:hover::after': { width: '100%' },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        bottom: -3,
                        width: 0,
                        height: 1,
                        bgcolor: INK,
                        transition: 'width 0.3s ease',
                      },
                    }}
                  >
                    {l.label}
                  </Box>
                ))}
              </Stack>
            )}

            {!isMobile ? (
              <Stack direction="row" spacing={1.5}>
                <Button component={RouterLink} to="/login" sx={pillBtn('ghost')}>
                  Ingresar
                </Button>
                <Button component={RouterLink} to="/register" sx={pillBtn('solid')} endIcon={<ArrowOutward sx={{ fontSize: 16 }} />}>
                  Empezar gratis
                </Button>
              </Stack>
            ) : (
              <IconButton onClick={() => setOpen(true)} aria-label="Menú" sx={{ color: INK }}>
                <MenuIcon />
              </IconButton>
            )}
          </Stack>
        </Container>
      </Box>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { bgcolor: CREAM, width: 300 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.3rem' }}>
            Dash<Box component="span" sx={{ fontStyle: 'italic', color: YELLOW_DEEP }}>Flow</Box>
          </Typography>
          <IconButton onClick={() => setOpen(false)} sx={{ color: INK }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ borderColor: RULE }} />
        <List>
          {NAV_LINKS.map((l) => (
            <ListItemButton key={l.href} component="a" href={l.href} onClick={() => setOpen(false)}>
              <ListItemText primaryTypographyProps={{ sx: { fontFamily: SANS, fontSize: '1rem' } }} primary={l.label} />
            </ListItemButton>
          ))}
        </List>
        <Divider sx={{ borderColor: RULE }} />
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Button component={RouterLink} to="/login" sx={pillBtn('outline')} fullWidth onClick={() => setOpen(false)}>
            Ingresar
          </Button>
          <Button component={RouterLink} to="/register" sx={pillBtn('solid')} fullWidth onClick={() => setOpen(false)}>
            Empezar gratis
          </Button>
        </Stack>
      </Drawer>
    </>
  );
};

/* =============================================================================
 * HERO — editorial split: oversize headline + live oversell simulator
 * ========================================================================== */

/** Live-feeling stock terminal showing a sync event every few seconds. */
const StockTerminal: React.FC = () => {
  const [stock, setStock] = useState(7);
  const [lastChannel, setLastChannel] = useState<'MELI' | 'NUBE' | 'POS'>('MELI');
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setSyncing(true);
      setStock((s) => {
        const channels: Array<'MELI' | 'NUBE' | 'POS'> = ['MELI', 'NUBE', 'POS'];
        setLastChannel(channels[Math.floor(Math.random() * 3)]);
        const next = s <= 1 ? 7 : s - 1;
        return next;
      });
      setTimeout(() => setSyncing(false), 600);
    }, 2800);
    return () => clearInterval(id);
  }, []);

  const Channel = ({ code, label, active }: { code: string; label: string; active: boolean }) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 2,
        py: 1.3,
        borderBottom: `1px solid ${RULE}`,
        bgcolor: active ? 'rgba(246,208,88,0.18)' : 'transparent',
        transition: 'background 0.4s ease',
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <FiberManualRecord sx={{ fontSize: 8, color: active ? YELLOW_DEEP : '#16a34a' }} />
        <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.06em' }}>
          {code}
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: '0.84rem', color: 'rgba(10,10,10,0.65)' }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: active ? INK : 'rgba(10,10,10,0.5)' }}>
        {active ? 'SYNC →' : 'OK'}
      </Typography>
    </Stack>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: CREAM_SOFT,
        border: `1px solid ${INK}`,
        boxShadow: `8px 8px 0 ${INK}`,
        transform: { md: 'rotate(0.6deg)' },
      }}
    >
      {/* Header strip */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${INK}`, bgcolor: INK, color: CREAM_SOFT }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{
            width: 7, height: 7, borderRadius: '50%', bgcolor: '#34d399',
            animation: 'pulse 1.6s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.35 },
            },
          }} />
          <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', letterSpacing: '0.18em' }}>LIVE · INVENTORY</Typography>
        </Stack>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', opacity: 0.6 }}>SKU · REM-OV-L-BLK</Typography>
      </Stack>

      {/* Product */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${RULE}` }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', letterSpacing: '0.16em', color: 'rgba(10,10,10,0.5)', mb: 0.5 }}>
          PRODUCTO
        </Typography>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.45rem', lineHeight: 1.15, mb: 2 }}>
          Remera oversize negra, <Box component="span" sx={{ fontStyle: 'italic' }}>talle L</Box>
        </Typography>

        <Stack direction="row" alignItems="flex-end" spacing={2}>
          <Box>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', letterSpacing: '0.16em', color: 'rgba(10,10,10,0.5)' }}>
              STOCK DISPONIBLE
            </Typography>
            <motion.div
              key={stock}
              initial={{ scale: 1.12, color: YELLOW_DEEP }}
              animate={{ scale: 1, color: INK }}
              transition={{ duration: 0.5 }}
              style={{
                fontFamily: DISPLAY,
                fontSize: '4.2rem',
                lineHeight: 1,
                fontWeight: 400,
                marginTop: 4,
              }}
            >
              {stock}
            </motion.div>
          </Box>
          <Box sx={{ pb: 1.3 }}>
            <Stack direction="row" spacing={0.7} alignItems="center" sx={{ color: syncing ? YELLOW_DEEP : 'rgba(10,10,10,0.45)', transition: 'color 0.3s' }}>
              <Sync sx={{ fontSize: 14, animation: syncing ? 'spin 0.6s linear' : 'none', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
              <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem' }}>
                {syncing ? 'sincronizando…' : 'en tiempo real'}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Channels list */}
      <Box>
        <Channel code="MELI" label="MercadoLibre" active={lastChannel === 'MELI' && syncing} />
        <Channel code="NUBE" label="Tienda Nube" active={lastChannel === 'NUBE' && syncing} />
        <Channel code="POS " label="Mostrador" active={lastChannel === 'POS' && syncing} />
      </Box>

      {/* Footer */}
      <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.2, borderTop: `1px solid ${RULE}`, bgcolor: 'rgba(10,10,10,0.03)' }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: 'rgba(10,10,10,0.55)' }}>
          Última sync · 0.3 s
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: 'rgba(10,10,10,0.55)' }}>
          Canales · 3/3
        </Typography>
      </Stack>

      {/* Corner sticker */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -14,
          transform: 'rotate(8deg)',
          bgcolor: YELLOW,
          border: `1px solid ${INK}`,
          px: 1.5,
          py: 0.8,
          fontFamily: MONO,
          fontSize: '0.7rem',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          boxShadow: `3px 3px 0 ${INK}`,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        anti-oversell
      </Box>
    </Box>
  );
};

const Hero: React.FC = () => {
  return (
    <Box
      sx={{
        pt: { xs: 6, md: 8 },
        pb: { xs: 6, md: 10 },
        borderBottom: `1px solid ${RULE}`,
        position: 'relative',
        overflow: 'hidden',
        // Malla de gradiente: tinta y amarillo sangrando en el papel. Muy
        // desenfocada para que nunca compita con la bajada en serif.
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '-25%',
          zIndex: 0,
          pointerEvents: 'none',
          background: `
            radial-gradient(38% 42% at 14% 20%, ${YELLOW}c4 0%, transparent 62%),
            radial-gradient(30% 34% at 86% 10%, ${YELLOW_DEEP}96 0%, transparent 66%),
            radial-gradient(42% 46% at 80% 86%, rgba(10,10,10,0.20) 0%, transparent 64%),
            radial-gradient(30% 32% at 26% 94%, ${INDIGO}22 0%, transparent 62%)
          `,
          filter: 'blur(72px) saturate(115%)',
          animation: 'heroDrift 26s ease-in-out infinite alternate',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
        '@keyframes heroDrift': {
          '0%': { transform: 'translate3d(0,0,0) scale(1)' },
          '100%': { transform: 'translate3d(2.5%, -2%, 0) scale(1.09)' },
        },
        // Grano encima de todo el fondo: le devuelve el gesto impreso.
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          backgroundImage: GRAIN,
          opacity: 0.5,
          mixBlendMode: 'multiply',
        },
      }}
    >
      {/* Póster debajo del video: se ve mientras carga y es lo único que queda con
          prefers-reduced-motion. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          backgroundImage: `url(${HERO_POSTER})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'saturate(85%)',
        }}
      />
      <Box
        component="video"
        src={HERO_VIDEO}
        poster={HERO_POSTER}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          pointerEvents: 'none',
          filter: 'saturate(85%)',
          '@media (prefers-reduced-motion: reduce)': { display: 'none' },
        }}
      />

      {/* Velo crema: el titular en serif negro se lee siempre. En escritorio es
          horizontal (denso a la izquierda, liviano detrás de la tarjeta de inventario)
          para que el video se vea; en mobile, parejo. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          background: {
            xs: `linear-gradient(180deg, ${CREAM}e6 0%, ${CREAM}d1 55%, ${CREAM}ee 100%)`,
            md: `linear-gradient(90deg, ${CREAM}f2 0%, ${CREAM}e3 38%, ${CREAM}8f 66%, ${CREAM}4d 100%), linear-gradient(180deg, transparent 70%, ${CREAM}cc 100%)`,
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Date / dateline row — magazine mast */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: { xs: 5, md: 7 } }}
        >
          <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.18em', color: 'rgba(10,10,10,0.55)' }}>
            INVENTARIO · EDICIÓN ARGENTINA · 2026
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              width: 7, height: 7, borderRadius: '50%', bgcolor: YELLOW_DEEP,
              animation: 'pulseDot 1.8s ease-in-out infinite',
              '@keyframes pulseDot': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.45, transform: 'scale(0.85)' },
              },
            }} />
            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.18em' }}>
              AHORA VENDIENDO
            </Typography>
          </Stack>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.85fr' }, gap: { xs: 5, md: 8 }, alignItems: 'center' }}>
          {/* Left — headline */}
          <Box>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 1 },
                visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
              }}
            >
              {[
                <>Vendés en tres canales.</>,
                <>Te queda <Box component="em" sx={{ fontStyle: 'italic', fontWeight: 400, position: 'relative', display: 'inline-block' }}>
                  una unidad.
                  <Box
                    component="svg"
                    viewBox="0 0 300 20"
                    preserveAspectRatio="none"
                    sx={{ position: 'absolute', left: -4, right: -4, bottom: -6, width: 'calc(100% + 8px)', height: 14, zIndex: -1 }}
                  >
                    <motion.path
                      d="M 5 12 Q 80 4, 150 10 T 295 9"
                      stroke={YELLOW}
                      strokeWidth="14"
                      strokeLinecap="round"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.9, delay: 0.9, ease: 'easeOut' }}
                    />
                  </Box>
                </Box></>,
                <>Ahora elegí: <Box component="em" sx={{ fontStyle: 'italic' }}>¿a quién le fallás?</Box></>,
              ].map((line, i) => (
                <motion.div
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] } },
                  }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: DISPLAY,
                      fontSize: { xs: '2.8rem', sm: '3.6rem', md: '4.4rem', lg: '5.1rem' },
                      lineHeight: 0.96,
                      letterSpacing: '-0.02em',
                      color: INK,
                      fontWeight: 400,
                      mb: 0.5,
                    }}
                  >
                    {line}
                  </Typography>
                </motion.div>
              ))}
            </motion.div>

            <Reveal delay={0.6}>
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: { xs: '1.02rem', md: '1.14rem' },
                  lineHeight: 1.55,
                  color: 'rgba(10,10,10,0.72)',
                  maxWidth: 520,
                  mt: 4,
                  mb: 4,
                }}
              >
                DashFlow sincroniza tu stock entre el mostrador, MercadoLibre y Tienda Nube en tiempo real.
                Ventas, caja, facturación ARCA y reposición — un solo backoffice para que dejes de perseguir unidades.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/register"
                  sx={{ ...pillBtn('solid'), px: 3.5, py: 1.6, fontSize: '0.95rem' }}
                  endIcon={<ArrowOutward sx={{ fontSize: 16 }} />}
                >
                  Empezar gratis
                </Button>
                <Button component="a" href="#how" sx={{ ...pillBtn('outline'), px: 3.5, py: 1.6, fontSize: '0.95rem' }}>
                  Ver cómo funciona
                </Button>
              </Stack>

              <Stack direction="row" spacing={3} sx={{ mt: 4, flexWrap: 'wrap' }} useFlexGap>
                {['Sin tarjeta', 'Setup el mismo día', 'Soporte en español'].map((t) => (
                  <Stack key={t} direction="row" spacing={0.8} alignItems="center">
                    <Box sx={{ width: 5, height: 5, bgcolor: INK, borderRadius: '50%' }} />
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Reveal>
          </Box>

          {/* Right — live terminal */}
          <Reveal delay={0.4}>
            <StockTerminal />
          </Reveal>
        </Box>
      </Container>
    </Box>
  );
};

/* =============================================================================
 * TICKER — infinite marquee
 * ========================================================================== */
const Ticker: React.FC = () => {
  const items = [...TICKER, ...TICKER];
  return (
    <Box sx={{ bgcolor: INK, color: CREAM_SOFT, py: 2.2, overflow: 'hidden', position: 'relative', borderBottom: `1px solid ${INK_SOFT}` }}>
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          whiteSpace: 'nowrap',
          animation: 'marq 38s linear infinite',
          width: 'max-content',
          '@keyframes marq': {
            from: { transform: 'translateX(0)' },
            to: { transform: 'translateX(-50%)' },
          },
        }}
      >
        {items.map((t, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 6, height: 6, bgcolor: YELLOW, borderRadius: '50%', flexShrink: 0 }} />
            <Typography sx={{ fontFamily: MONO, fontSize: '0.82rem', letterSpacing: '0.12em' }}>{t}</Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

/* =============================================================================
 * PROBLEM — before / after comparison panels
 * ========================================================================== */
const Problem: React.FC = () => {
  return (
    <Section bg={CREAM} id="problem">
      <Reveal>
        <SectionLabel idx="§ 01">El problema</SectionLabel>
        <Typography
          sx={{
            fontFamily: DISPLAY,
            fontSize: { xs: '2.2rem', md: '3.4rem' },
            lineHeight: 1.02,
            letterSpacing: '-0.02em',
            maxWidth: 920,
            mb: { xs: 6, md: 9 },
          }}
        >
          Tu <Box component="em" sx={{ fontStyle: 'italic', color: YELLOW_DEEP }}>stock real</Box> vive en tu cabeza.{' '}
          Y tu cabeza no está conectada a MercadoLibre.
        </Typography>
      </Reveal>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' }, gap: { xs: 3, md: 3 }, alignItems: 'stretch' }}>
        {/* BEFORE */}
        <Reveal>
          <Box sx={{ border: `1px solid ${RULE_HARD}`, bgcolor: CREAM_SOFT, p: 3.5, height: '100%', position: 'relative' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.18em', opacity: 0.6 }}>
                SIN DASHFLOW
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.14em', color: '#b91c1c' }}>
                ERROR · 3 FUENTES
              </Typography>
            </Stack>
            <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.8rem', lineHeight: 1.15, mb: 3 }}>
              ¿Cuántas remeras L quedan?
            </Typography>
            <Stack spacing={1.2}>
              {[
                { ch: 'Excel depósito', val: '3', color: '#b91c1c' },
                { ch: 'MercadoLibre', val: '5', color: '#b91c1c' },
                { ch: 'Tienda Nube', val: '1', color: '#b91c1c' },
                { ch: 'WhatsApp · vendedora', val: '¿4?', color: '#b91c1c' },
              ].map((row) => (
                <Stack key={row.ch} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.2, bgcolor: 'rgba(185,28,28,0.06)', border: `1px solid rgba(185,28,28,0.15)` }}>
                  <Typography sx={{ fontFamily: SANS, fontSize: '0.9rem' }}>{row.ch}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: '1rem', fontWeight: 500, color: row.color }}>{row.val}</Typography>
                </Stack>
              ))}
            </Stack>
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px dashed ${RULE_HARD}` }}>
              <Typography sx={{ fontFamily: SANS, fontSize: '0.86rem', fontStyle: 'italic', color: 'rgba(10,10,10,0.6)' }}>
                Resultado: reputación verde se vuelve amarilla, clientes que no vuelven,
                 producción que no entiende qué fabricar.
              </Typography>
            </Box>
          </Box>
        </Reveal>

        {/* Divider arrow */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: '1.4rem', color: 'rgba(10,10,10,0.55)' }}>
              con
            </Typography>
            <Box
              sx={{
                width: 46,
                height: 46,
                mx: 'auto',
                my: 1,
                bgcolor: YELLOW,
                border: `1px solid ${INK}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'rotate(-6deg)',
                boxShadow: `3px 3px 0 ${INK}`,
              }}
            >
              <NorthEast sx={{ fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: '1.4rem' }}>
              DashFlow
            </Typography>
          </Box>
        </Box>

        {/* AFTER */}
        <Reveal delay={0.12}>
          <Box sx={{ border: `1px solid ${INK}`, bgcolor: INK, color: CREAM_SOFT, p: 3.5, height: '100%', position: 'relative' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.18em', opacity: 0.55 }}>
                CON DASHFLOW
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.14em', color: YELLOW }}>
                OK · 1 FUENTE
              </Typography>
            </Stack>
            <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.8rem', lineHeight: 1.15, mb: 3 }}>
              Quedan <Box component="em" sx={{ fontStyle: 'italic', color: YELLOW }}>3 remeras L.</Box>
            </Typography>
            <Stack spacing={1.2}>
              {[
                { ch: 'MercadoLibre', val: '3' },
                { ch: 'Tienda Nube', val: '3' },
                { ch: 'Mostrador', val: '3' },
                { ch: 'Depósito', val: '3' },
              ].map((row) => (
                <Stack key={row.ch} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography sx={{ fontFamily: SANS, fontSize: '0.9rem' }}>{row.ch}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: '1rem', fontWeight: 500, color: YELLOW }}>{row.val}</Typography>
                </Stack>
              ))}
            </Stack>
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px dashed rgba(255,255,255,0.18)` }}>
              <Typography sx={{ fontFamily: SANS, fontSize: '0.86rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
                Resultado: cada canal descuenta del mismo tacho. Producción sabe qué falta.
                Vos vendés más, dormís mejor.
              </Typography>
            </Box>
          </Box>
        </Reveal>
      </Box>
    </Section>
  );
};

/* =============================================================================
 * FEATURES — editorial numbered spreads
 * ========================================================================== */
const Features: React.FC = () => {
  return (
    <Section bg={CREAM_SOFT} id="features">
      <Reveal>
        <SectionLabel idx="§ 02">Producto</SectionLabel>
        <Typography
          sx={{
            fontFamily: DISPLAY,
            fontSize: { xs: '2.2rem', md: '3.4rem' },
            lineHeight: 1.02,
            letterSpacing: '-0.02em',
            maxWidth: 900,
            mb: { xs: 7, md: 10 },
          }}
        >
          Todo el backoffice de tu PyME en un solo sistema —{' '}
          <Box component="em" sx={{ fontStyle: 'italic', color: 'rgba(10,10,10,0.55)' }}>no en cinco planillas.</Box>
        </Typography>
      </Reveal>

      <Stack spacing={{ xs: 7, md: 10 }}>
        {FEATURES.map((f, i) => {
          const reverse = i % 2 === 1;
          return (
            <Reveal key={f.n}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' },
                  gap: { xs: 3, md: 8 },
                  alignItems: 'start',
                  direction: reverse ? 'rtl' : 'ltr',
                }}
              >
                {/* Number side */}
                <Box sx={{ direction: 'ltr', borderTop: `1px solid ${RULE_HARD}`, pt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontFamily: DISPLAY,
                        fontStyle: 'italic',
                        fontSize: { xs: '4.5rem', md: '6.5rem' },
                        lineHeight: 0.9,
                        color: INK,
                        fontWeight: 400,
                      }}
                    >
                      {f.n}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ pb: 1 }}>
                      <Box sx={{ color: INK, opacity: 0.75 }}>{f.icon}</Box>
                      <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.16em', textTransform: 'uppercase' }}>
                        {f.tag}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack spacing={1.2}>
                    {f.bullets.map((b) => (
                      <Stack key={b} direction="row" spacing={1.2} alignItems="baseline">
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: 'rgba(10,10,10,0.45)', flexShrink: 0, width: 20 }}>
                          ·
                        </Typography>
                        <Typography sx={{ fontFamily: SANS, fontSize: '0.95rem' }}>{b}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                {/* Text side */}
                <Box sx={{ direction: 'ltr' }}>
                  <Typography
                    sx={{
                      fontFamily: DISPLAY,
                      fontSize: { xs: '1.9rem', md: '2.6rem' },
                      lineHeight: 1.08,
                      letterSpacing: '-0.015em',
                      mb: 2,
                    }}
                  >
                    {f.title}
                  </Typography>
                  <Typography sx={{ fontFamily: SANS, fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.6, color: 'rgba(10,10,10,0.72)', maxWidth: 560 }}>
                    {f.body}
                  </Typography>
                </Box>
              </Box>
            </Reveal>
          );
        })}
      </Stack>
    </Section>
  );
};

/* =============================================================================
 * HOW IT WORKS — technical diagram, 3 steps
 * ========================================================================== */
const HowItWorks: React.FC = () => {
  return (
    <Section bg={CREAM} id="how">
      <Reveal>
        <SectionLabel idx="§ 03">Cómo funciona</SectionLabel>
        <Typography
          sx={{
            fontFamily: DISPLAY,
            fontSize: { xs: '2.2rem', md: '3.4rem' },
            lineHeight: 1.02,
            letterSpacing: '-0.02em',
            maxWidth: 860,
            mb: { xs: 6, md: 9 },
          }}
        >
          Operativo el <Box component="em" sx={{ fontStyle: 'italic' }}>mismo día</Box>. Sin call-centers ni migraciones de seis meses.
        </Typography>
      </Reveal>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 3, md: 0 }, position: 'relative' }}>
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.12}>
            <Box
              sx={{
                position: 'relative',
                p: { xs: 3, md: 4 },
                borderTop: `1px solid ${INK}`,
                borderRight: { md: i < STEPS.length - 1 ? `1px solid ${RULE}` : 'none', xs: 'none' },
                borderLeft: { xs: 'none', md: i === 0 ? 'none' : 'none' },
                minHeight: 300,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    fontFamily: DISPLAY,
                    fontStyle: 'italic',
                    fontSize: '3rem',
                    lineHeight: 0.9,
                    fontWeight: 400,
                  }}
                >
                  {s.n}
                </Typography>
                <Box
                  sx={{
                    px: 1.3,
                    py: 0.5,
                    bgcolor: YELLOW,
                    border: `1px solid ${INK}`,
                    fontFamily: MONO,
                    fontSize: '0.68rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}
                >
                  {s.time}
                </Box>
              </Stack>
              <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.7rem', lineHeight: 1.1, mb: 1.5 }}>
                {s.title}
              </Typography>
              <Typography sx={{ fontFamily: SANS, fontSize: '0.98rem', lineHeight: 1.6, color: 'rgba(10,10,10,0.68)' }}>
                {s.body}
              </Typography>

              {/* Connecting arrow on desktop */}
              {i < STEPS.length - 1 && (
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    position: 'absolute',
                    right: -12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 24,
                    height: 24,
                    bgcolor: CREAM,
                    border: `1px solid ${INK}`,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                  }}
                >
                  <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.8rem', lineHeight: 1 }}>→</Box>
                </Box>
              )}
            </Box>
          </Reveal>
        ))}
      </Box>
    </Section>
  );
};

/* =============================================================================
 * INTEGRATIONS STRIP
 * ========================================================================== */
const Integrations: React.FC = () => {
  const items = [
    { icon: <ShoppingBag />, label: 'MercadoLibre', sub: 'FULL · MARKETPLACE · QQ' },
    { icon: <Storefront />, label: 'Tienda Nube', sub: 'TIENDA PROPIA · CHECKOUT' },
    { icon: <WhatsApp />, label: 'Agente IA de WhatsApp', sub: 'CONSULTAS DE STOCK Y PRECIOS · PLAN IA' },
  ];
  return (
    <Box sx={{ py: { xs: 5, md: 6 }, bgcolor: CREAM_SOFT, borderTop: `1px solid ${RULE}`, borderBottom: `1px solid ${RULE}` }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={3}>
          <Typography sx={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: '1.3rem', color: 'rgba(10,10,10,0.7)' }}>
            Conectado con los canales que ya usás:
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, md: 4 }} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            {items.map((it) => (
              <Stack key={it.label} direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ color: INK }}>{it.icon}</Box>
                <Box>
                  <Typography sx={{ fontFamily: SANS, fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.2 }}>{it.label}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.12em', color: 'rgba(10,10,10,0.5)' }}>{it.sub}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

/* =============================================================================
 * PRICING · DEMO, PLAN A MEDIDA Y COMPARATIVA (cards #23, #24 y #25 del Trello)
 * ---------------------------------------------------------------------------
 * El formulario manda el lead al CRM de sales-hub (origen LandingForm): queda con
 * vendedor asignado y le escribimos por WhatsApp. La tabla es espejo de las
 * features de GET /api/subscription-plans al 02/11/2026: si cambian los planes en
 * la base, actualizar COMPARISON y CARD_HIGHLIGHTS.
 * ========================================================================== */
const LANDING_LEADS_URL = 'https://api.sales.efcloud.tech/api/public/landing-leads';
const DEMO_CALENDAR_URL = 'https://calendly.com/efcloud/demo';
const CUSTOM_PLAN = 'A medida';
const UNDECIDED_PLAN = 'Todavía no sé';
const SIZE_OPTIONS = ['1 local o punto de venta', '2 a 3 locales', '4 a 10 locales', 'Más de 10 locales', 'Vendo solo online'];

/** Diferenciales de cada tarjeta: el detalle completo vive en la tabla comparativa. */
const CARD_HIGHLIGHTS: Record<string, string[]> = {
  free: ['Ventas con lector de código de barras', 'Gestión de stock', 'Hasta 100 productos · 1 usuario', 'Gratis para siempre, sin tarjeta'],
  esencial: ['Productos por unidad, peso, variantes y combos', 'Clientes y cuentas corrientes', 'Reportes de ventas diarios', 'Hasta 3.500 productos · 2 usuarios'],
  pro: ['Todo lo del Plan Esencial', 'Soporte para balanzas', 'Hasta 10.000 productos · 5 usuarios'],
  ia: ['Todo lo del Plan Pro', 'Agente IA de WhatsApp: stock y precios por chat', 'Escaneo inteligente de facturas', 'Precios y productos sugeridos'],
};

type CompareCell = boolean | string;
interface CompareRow {
  label: string;
  cells: Record<string, CompareCell>;
}
const COMPARISON: Array<{ group: string; rows: CompareRow[] }> = [
  {
    group: 'Ventas y caja',
    rows: [
      { label: 'Ventas rápidas con lector de código de barras o teclado', cells: { free: true, esencial: true, pro: true, ia: true } },
      { label: 'Productos por unidad, peso, variantes y combos', cells: { free: false, esencial: true, pro: true, ia: true } },
      { label: 'Reportes de ventas e ingresos diarios', cells: { free: false, esencial: true, pro: true, ia: true } },
      { label: 'Clientes y cuentas corrientes', cells: { free: false, esencial: true, pro: true, ia: true } },
    ],
  },
  {
    group: 'Stock',
    rows: [
      { label: 'Gestión de stock', cells: { free: true, esencial: true, pro: true, ia: true } },
      { label: 'Reposición', cells: { free: false, esencial: true, pro: true, ia: true } },
      { label: 'Soporte para balanzas', cells: { free: false, esencial: false, pro: true, ia: true } },
    ],
  },
  {
    group: 'Facturación',
    rows: [
      { label: 'Facturación electrónica de ARCA', cells: { free: false, esencial: 'Costo adicional', pro: 'Costo adicional', ia: 'Costo adicional' } },
    ],
  },
  {
    group: 'Inteligencia',
    rows: [
      { label: 'Agente IA de WhatsApp: consultá stock y precios por chat', cells: { free: false, esencial: false, pro: false, ia: true } },
      { label: 'Escaneo inteligente de facturas', cells: { free: false, esencial: false, pro: false, ia: true } },
      { label: 'Productos sugeridos automáticamente', cells: { free: false, esencial: false, pro: false, ia: true } },
      { label: 'Precios sugeridos inteligentemente', cells: { free: false, esencial: false, pro: false, ia: true } },
      { label: 'Detecta productos que no se venden', cells: { free: false, esencial: false, pro: false, ia: true } },
    ],
  },
  {
    group: 'Capacidad',
    rows: [
      { label: 'Productos', cells: { free: '100', esencial: '3.500', pro: '10.000', ia: '10.000' } },
      { label: 'Usuarios', cells: { free: '1', esencial: '2', pro: '5', ia: '5' } },
    ],
  },
];

const scrollToDemo = () => {
  const el = document.getElementById('cotizar');
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.setTimeout(() => (document.getElementById('demo-name') as HTMLInputElement | null)?.focus({ preventScroll: true }), 600);
};

const fieldLabelSx = { fontFamily: MONO, fontSize: '0.66rem', letterSpacing: '0.16em', textTransform: 'uppercase' as const, opacity: 0.7, mb: 0.8, display: 'block' };
const fieldSx = {
  width: '100%',
  boxSizing: 'border-box' as const,
  bgcolor: CREAM_SOFT,
  color: INK,
  border: `1px solid ${INK}`,
  borderRadius: 0,
  px: 1.6,
  py: 1.3,
  fontFamily: SANS,
  fontSize: '1rem',
  outline: 'none',
  appearance: 'none' as const,
  '&:focus': { boxShadow: `3px 3px 0 ${YELLOW_DEEP}` },
};

type DemoStatus = 'idle' | 'sending' | 'done' | 'error';

/** Marcas de sesión compartidas por el formulario y el popup de salida. */
const DEMO_SENT_KEY = 'df_demo_sent';
const DEMO_TOUCHED_KEY = 'df_demo_touched';
const sessionFlag = (k: string): boolean => { try { return sessionStorage.getItem(k) === '1'; } catch { return false; } };
const setSessionFlag = (k: string) => { try { sessionStorage.setItem(k, '1'); } catch { /* modo privado */ } };

/**
 * Formulario de demo (card #24). Lo usan la sección de precios y el popup "¿Antes de
 * irte?" (card #22): mismo envío a sales-hub, con ids propios (idPrefix) y la página
 * marcada con pageSuffix para distinguir de dónde vino el lead.
 */
const DemoLeadForm: React.FC<{
  planOptions: string[];
  plan: string;
  onPlanChange: (p: string) => void;
  idPrefix?: string;
  pageSuffix?: string;
  /** "callback": popup de salida (nombre, WhatsApp y mail; te llamamos). */
  variant?: 'demo' | 'callback';
}> = ({ planOptions, plan, onPlanChange, idPrefix = 'demo', pageSuffix = '', variant = 'demo' }) => {
  const callback = variant === 'callback';
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [business, setBusiness] = useState('');
  const [size, setSize] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<DemoStatus>('idle');
  const [error, setError] = useState('');
  const fid = (f: string) => `${idPrefix}-${f}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (name.trim().length < 2) { setStatus('error'); setError('Decinos tu nombre.'); return; }
    if (phone.replace(/\D/g, '').length < 8) { setStatus('error'); setError('Revisá el WhatsApp: con código de área, por ejemplo 11 2345 6789.'); return; }
    if (callback && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setStatus('error'); setError('Revisá tu mail.'); return; }
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(LANDING_LEADS_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          productKey: 'unistock',
          name: name.trim(),
          phone: phone.trim(),
          email: callback ? email.trim() : undefined,
          business: callback ? undefined : business.trim() || undefined,
          size: callback ? undefined : size || undefined,
          plan: callback ? undefined : plan || undefined,
          page: window.location.pathname + pageSuffix,
          website,
        }),
      });
      if (res.ok) {
        setStatus('done');
        setSessionFlag(DEMO_SENT_KEY);
        trackFunnel('Lead');
        return;
      }
      let msg = res.status === 429 ? 'Recibimos muchos pedidos desde tu conexión. Probá en unos minutos o escribinos por WhatsApp.' : 'No pudimos enviar tus datos.';
      if (res.status === 400) {
        try { const j = await res.json(); if (j?.error) msg = String(j.error); } catch { /* sin cuerpo */ }
      }
      setStatus('error');
      setError(msg);
    } catch {
      setStatus('error');
      setError('No pudimos enviar tus datos. Revisá tu conexión.');
    }
  };

  if (status === 'done' && callback) {
    return (
      <Stack spacing={2} role="status" sx={{ py: 2 }}>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: '2.2rem', lineHeight: 1.05 }}>
          ¡Listo! <Box component="em" sx={{ fontStyle: 'italic' }}>Te llamamos en un rato.</Box>
        </Typography>
      </Stack>
    );
  }

  if (status === 'done') {
    return (
      <Stack spacing={2} sx={{ height: '100%', justifyContent: 'center', py: { xs: 2, md: 4 } }}>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: '2.2rem', lineHeight: 1.05 }}>
          ¡Listo! <Box component="em" sx={{ fontStyle: 'italic' }}>Te escribimos por WhatsApp.</Box>
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: '0.98rem', color: 'rgba(10,10,10,0.68)' }}>
          Si ya querés dejar la demo agendada, elegí día y hora.
        </Typography>
        <Box>
          <Button href={DEMO_CALENDAR_URL} target="_blank" rel="noopener" sx={pillBtn('outline')} endIcon={<ArrowOutward sx={{ fontSize: 16 }} />}>
            Elegir día y hora
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Box component="form" onSubmit={submit} onFocusCapture={() => setSessionFlag(DEMO_TOUCHED_KEY)} noValidate sx={{ position: 'relative' }}>
      <Stack spacing={2}>
        <Box>
          <Box component="label" htmlFor={fid('name')} sx={fieldLabelSx}>Nombre *</Box>
          <Box component="input" id={fid('name')} name="name" autoComplete="name" required value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} sx={fieldSx} />
        </Box>
        <Box>
          <Box component="label" htmlFor={fid('phone')} sx={fieldLabelSx}>WhatsApp *</Box>
          <Box component="input" id={fid('phone')} name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="11 2345 6789" required value={phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)} sx={fieldSx} />
        </Box>
        {callback ? (
        <Box>
          <Box component="label" htmlFor={fid('email')} sx={fieldLabelSx}>Email *</Box>
          <Box component="input" id={fid('email')} name="email" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} sx={fieldSx} />
        </Box>
        ) : (<>
        <Box>
          <Box component="label" htmlFor={fid('business')} sx={fieldLabelSx}>Nombre del comercio</Box>
          <Box component="input" id={fid('business')} name="business" autoComplete="organization" value={business} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBusiness(e.target.value)} sx={fieldSx} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Box component="label" htmlFor={fid('size')} sx={fieldLabelSx}>Tamaño</Box>
            <Box component="select" id={fid('size')} name="size" value={size} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSize(e.target.value)} sx={fieldSx}>
              <option value="">Elegí una opción</option>
              {SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Box>
          </Box>
          <Box>
            <Box component="label" htmlFor={fid('plan')} sx={fieldLabelSx}>Plan de interés</Box>
            <Box component="select" id={fid('plan')} name="plan" value={plan} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onPlanChange(e.target.value)} sx={fieldSx}>
              <option value="">Elegí una opción</option>
              {planOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </Box>
          </Box>
        </Box>
        </>)}
        {/* Honeypot: invisible para personas; si viene con texto, el backend lo descarta. */}
        <Box
          component="input"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWebsite(e.target.value)}
          sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }}
        />
        {status === 'error' && (
          <Typography role="alert" sx={{ fontFamily: SANS, fontSize: '0.9rem', color: '#b91c1c' }}>
            {error}{' '}
            <Box component="a" href={whatsappLink('Hola! Quiero una demo de DashFlow')} target="_blank" rel="noopener" sx={{ color: INK, fontWeight: 600 }}>
              Escribinos por WhatsApp
            </Box>
          </Typography>
        )}
        <Button type="submit" disabled={status === 'sending'} sx={{ ...pillBtn('solid'), '&.Mui-disabled': { bgcolor: INK_SOFT, color: 'rgba(250,246,236,0.6)' } }} endIcon={<ArrowOutward sx={{ fontSize: 16 }} />} fullWidth>
          {status === 'sending' ? 'Enviando…' : callback ? 'Quiero que me llamen' : 'Quiero mi demo'}
        </Button>
      </Stack>
    </Box>
  );
};

const DemoLeadBlock: React.FC<{ planOptions: string[]; plan: string; onPlanChange: (p: string) => void }> = ({ planOptions, plan, onPlanChange }) => (
  <Reveal>
    <Box
      id="cotizar"
      sx={{
        scrollMarginTop: 96,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        bgcolor: INK,
        color: CREAM_SOFT,
        border: `1px solid ${INK}`,
        boxShadow: `6px 6px 0 ${YELLOW}`,
        mb: { xs: 8, md: 10 },
      }}
    >
      <Box sx={{ p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', letterSpacing: '0.2em', color: YELLOW, mb: 2.5 }}>
          DEMO GRATIS · COTIZACIÓN A MEDIDA
        </Typography>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '2rem', md: '2.8rem' }, lineHeight: 1.04, letterSpacing: '-0.01em', mb: 2.5 }}>
          ¿Lo querés ver con tu comercio?{' '}
          <Box component="em" sx={{ fontStyle: 'italic', color: YELLOW }}>Pedí una demo o cotizá a medida.</Box>
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: '1rem', lineHeight: 1.6, color: 'rgba(250,246,236,0.75)', maxWidth: 440 }}>
          Dejanos tus datos y te escribimos por WhatsApp para coordinar. Te mostramos cómo quedaría DashFlow con tus canales y, si tu operación es grande, te armamos una propuesta.
        </Typography>
      </Box>

      <Box sx={{ bgcolor: CREAM_SOFT, color: INK, p: { xs: 3, md: 4 }, borderLeft: { md: `1px solid ${INK}` }, borderTop: { xs: `1px solid ${INK}`, md: 'none' } }}>
        <DemoLeadForm planOptions={planOptions} plan={plan} onPlanChange={onPlanChange} />
      </Box>
    </Box>
  </Reveal>
);

/** Plan "A medida": sin precio publicado, se cotiza por el formulario. */
const CustomPlanCard: React.FC<{ topPlanName: string; onQuote: () => void }> = ({ topPlanName, onQuote }) => (
  <Reveal>
    <Box
      sx={{
        border: `1px solid ${INK}`,
        bgcolor: CREAM,
        p: { xs: 3, md: 4 },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.1fr 1.2fr auto' },
        gap: { xs: 3, md: 5 },
        alignItems: 'center',
      }}
    >
      <Box>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', letterSpacing: '0.2em', opacity: 0.7, mb: 1.5 }}>PLAN A MEDIDA</Typography>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '2.2rem', md: '2.6rem' }, lineHeight: 1, mb: 1 }}>
          Consultá el <Box component="em" sx={{ fontStyle: 'italic' }}>precio</Box>
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontStyle: 'italic', fontSize: '0.95rem', color: 'rgba(10,10,10,0.65)' }}>
          Para cadenas con varios locales, mayoristas y fábricas.
        </Typography>
      </Box>
      <Stack spacing={1.2}>
        {[`Todo lo del Plan ${topPlanName}`, 'Precio según tu operación: locales, usuarios y volumen', 'Propuesta por WhatsApp'].map((b) => (
          <Stack key={b} direction="row" spacing={1.2} alignItems="baseline">
            <Box sx={{ width: 6, height: 6, mt: 0.6, bgcolor: INK, flexShrink: 0 }} />
            <Typography sx={{ fontFamily: SANS, fontSize: '0.93rem' }}>{b}</Typography>
          </Stack>
        ))}
      </Stack>
      <Button onClick={onQuote} sx={pillBtn('solid')} endIcon={<ArrowOutward sx={{ fontSize: 16 }} />}>
        Cotizar
      </Button>
    </Box>
  </Reveal>
);

const CompareMark: React.FC<{ value: CompareCell | undefined }> = ({ value }) => {
  if (value === true) return <Box component="span" aria-label="Incluido" sx={{ fontFamily: SANS, fontWeight: 600 }}>✓</Box>;
  if (value === false || value === undefined) return <Box component="span" aria-label="No incluido" sx={{ opacity: 0.35 }}>—</Box>;
  return <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.78rem' }}>{value}</Box>;
};

/** Tabla comparativa por categoría. Columnas: los planes de la API + A medida. */
const PlanComparison: React.FC<{ plans: LandingPlan[] }> = ({ plans }) => {
  const stickyCell = { position: 'sticky' as const, left: 0, zIndex: 1, bgcolor: CREAM_SOFT };
  const cellSx = { borderBottom: `1px solid ${RULE}`, px: 1.5, py: 1.4, textAlign: 'center' as const, fontFamily: SANS, fontSize: '0.9rem' };
  return (
    <Box id="comparar-planes" sx={{ scrollMarginTop: 96, mt: { xs: 8, md: 10 } }}>
      <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '1.9rem', md: '2.6rem' }, lineHeight: 1.05, mb: 3 }}>
        Compará <Box component="em" sx={{ fontStyle: 'italic' }}>todos</Box> los planes
      </Typography>
      <Box sx={{ overflowX: 'auto', border: `1px solid ${INK}`, bgcolor: CREAM_SOFT, WebkitOverflowScrolling: 'touch' }}>
        <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 760 }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ ...cellSx, ...stickyCell, textAlign: 'left', minWidth: 210, borderBottom: `1px solid ${INK}` }} />
              {plans.map((p) => (
                <Box component="th" key={p.code} sx={{ ...cellSx, borderBottom: `1px solid ${INK}`, verticalAlign: 'bottom', bgcolor: p.highlight ? INK : undefined, color: p.highlight ? CREAM_SOFT : INK }}>
                  {p.highlight && <Box sx={{ fontFamily: MONO, fontSize: '0.6rem', letterSpacing: '0.16em', color: YELLOW, mb: 0.5 }}>MÁS ELEGIDO</Box>}
                  <Box sx={{ fontFamily: DISPLAY, fontSize: '1.5rem', fontWeight: 400, lineHeight: 1 }}>{p.name}</Box>
                  <Box sx={{ fontFamily: MONO, fontSize: '0.72rem', opacity: 0.75, mt: 0.6, whiteSpace: 'nowrap' }}>{p.price > 0 ? `$${fmtARS(p.price)}/mes` : '$0'}</Box>
                </Box>
              ))}
              <Box component="th" sx={{ ...cellSx, borderBottom: `1px solid ${INK}`, verticalAlign: 'bottom' }}>
                <Box sx={{ fontFamily: DISPLAY, fontSize: '1.5rem', fontWeight: 400, lineHeight: 1 }}>{CUSTOM_PLAN}</Box>
                <Box sx={{ fontFamily: MONO, fontSize: '0.72rem', opacity: 0.75, mt: 0.6, whiteSpace: 'nowrap' }}>Consultá</Box>
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {COMPARISON.map((g) => (
              <React.Fragment key={g.group}>
                <Box component="tr">
                  <Box component="th" colSpan={plans.length + 2} sx={{ ...cellSx, textAlign: 'left', bgcolor: CREAM, fontFamily: MONO, fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', py: 1.1 }}>
                    <Box component="span" sx={{ position: 'sticky', left: 12 }}>{g.group}</Box>
                  </Box>
                </Box>
                {g.rows.map((r) => (
                  <Box component="tr" key={r.label}>
                    <Box component="th" scope="row" sx={{ ...cellSx, ...stickyCell, textAlign: 'left', fontWeight: 400, borderRight: `1px solid ${RULE}` }}>{r.label}</Box>
                    {plans.map((p) => (
                      <Box component="td" key={p.code} sx={{ ...cellSx, bgcolor: p.highlight ? 'rgba(246,208,88,0.14)' : undefined }}>
                        <CompareMark value={r.cells[p.code]} />
                      </Box>
                    ))}
                    <Box component="td" sx={cellSx}>
                      <CompareMark value={Object.values(r.cells).some((c) => typeof c === 'string') ? CUSTOM_PLAN : true} />
                    </Box>
                  </Box>
                ))}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ fontFamily: SANS, fontSize: '0.85rem', color: 'rgba(10,10,10,0.6)', mt: 2 }}>
        Precios en pesos argentinos, por mes. La facturación electrónica de ARCA tiene costo adicional.
      </Typography>
    </Box>
  );
};

/* =============================================================================
 * PRICING — library catalog cards
 * ========================================================================== */
const Pricing: React.FC = () => {
  const [plans, setPlans] = useState<LandingPlan[]>(PLANS);
  const [demoPlan, setDemoPlan] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Mismo endpoint público que usa PricingPage.tsx.
        const api = (apiService as any).api;
        const res = await api.get('/subscription-plans');
        const fetched: LandingPlan[] = (res.data || []).map((p: any) => ({
          code: p.code,
          name: String(p.name || '').replace('Plan ', ''),
          price: p.price,
          blurb: p.description || '',
          bullets: p.features || [],
        }));
        // Destacado: siempre el primer plan pago (el que sigue al Gratis), no el isPopular de la base.
        const firstPaid = fetched.findIndex((p) => p.price > 0);
        fetched.forEach((p, i) => { p.highlight = i === firstPaid; });
        if (alive && fetched.length > 0) setPlans(fetched);
      } catch {
        // Silencioso: quedan los planes reales hardcodeados como fallback.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Section bg={CREAM_SOFT} id="pricing">
      <Reveal>
        <SectionLabel idx="§ 04">Precio</SectionLabel>
      </Reveal>

      <DemoLeadBlock
        planOptions={[...plans.map((pl) => pl.name), CUSTOM_PLAN, UNDECIDED_PLAN]}
        plan={demoPlan}
        onPlanChange={setDemoPlan}
      />

      <Reveal>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-end' }} sx={{ mb: { xs: 6, md: 8 } }} spacing={3}>
          <Typography
            sx={{
              fontFamily: DISPLAY,
              fontSize: { xs: '2.2rem', md: '3.4rem' },
              lineHeight: 1.02,
              letterSpacing: '-0.02em',
              maxWidth: 700,
            }}
          >
            Empezá <Box component="em" sx={{ fontStyle: 'italic' }}>gratis</Box>.
            Sin tarjeta. Sin trampa.
          </Typography>
          <Typography sx={{ fontFamily: SANS, fontSize: '0.95rem', color: 'rgba(10,10,10,0.65)', maxWidth: 280 }}>
            Precios en pesos argentinos. Facturación electrónica de ARCA disponible con costo adicional.
          </Typography>
        </Stack>
      </Reveal>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: `repeat(${Math.min(plans.length, 4)}, 1fr)` }, gap: 2.5, alignItems: 'stretch' }}>
        {plans.map((p, i) => {
          const dark = p.highlight;
          const isFree = p.code === 'free' || p.price === 0;
          return (
            <Reveal key={p.code} delay={i * 0.1}>
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  bgcolor: dark ? INK : CREAM,
                  color: dark ? CREAM_SOFT : INK,
                  border: `1px solid ${INK}`,
                  p: { xs: 3, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.4s cubic-bezier(0.21,0.47,0.32,0.98)',
                  '&:hover': { transform: 'translateY(-6px)' },
                  ...(dark && { boxShadow: `6px 6px 0 ${YELLOW}` }),
                }}
              >
                {/* Top stub like a library card */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, pb: 1.5, borderBottom: dark ? '1px solid rgba(255,255,255,0.2)' : `1px dashed ${RULE_HARD}` }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', letterSpacing: '0.2em', opacity: 0.7 }}>
                    PLAN {String(i + 1).padStart(2, '0')}
                  </Typography>
                  {p.highlight && (
                    <Box sx={{ bgcolor: YELLOW, color: INK, px: 1.2, py: 0.3, fontFamily: MONO, fontSize: '0.64rem', letterSpacing: '0.16em' }}>
                      MÁS ELEGIDO
                    </Box>
                  )}
                </Stack>

                <Typography sx={{ fontFamily: DISPLAY, fontSize: '2.4rem', lineHeight: 1, mb: 1 }}>{p.name}</Typography>
                <Typography sx={{ fontFamily: SANS, fontStyle: 'italic', fontSize: '0.95rem', color: dark ? 'rgba(255,255,255,0.75)' : 'rgba(10,10,10,0.65)', mb: 3, minHeight: 42 }}>
                  {p.blurb}
                </Typography>

                <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 3 }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.9rem', opacity: 0.7 }}>$</Typography>
                  <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '3.2rem', md: '3.6rem' }, lineHeight: 1 }}>{fmtARS(p.price)}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', opacity: 0.7 }}>ARS/mes</Typography>
                </Stack>

                <Stack spacing={1.2} sx={{ mb: 4, flex: 1 }}>
                  {(CARD_HIGHLIGHTS[p.code] ?? p.bullets.slice(0, 4)).map((b) => (
                    <Stack key={b} direction="row" spacing={1.2} alignItems="baseline">
                      <Box sx={{ width: 6, height: 6, mt: 0.6, bgcolor: dark ? YELLOW : INK, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: SANS, fontSize: '0.93rem' }}>{b}</Typography>
                    </Stack>
                  ))}
                </Stack>

                <Button
                  component={RouterLink}
                  to="/register"
                  sx={
                    dark
                      ? { ...pillBtn('solid'), bgcolor: YELLOW, color: INK, borderColor: YELLOW, '&:hover': { bgcolor: YELLOW_DEEP, borderColor: YELLOW_DEEP } }
                      : pillBtn('outline')
                  }
                  endIcon={<ArrowOutward sx={{ fontSize: 16 }} />}
                  fullWidth
                >
                  {isFree ? 'Empezar gratis' : `Empezar con ${p.name}`}
                </Button>
              </Box>
            </Reveal>
          );
        })}
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <CustomPlanCard
          topPlanName={[...plans].sort((a, b) => b.price - a.price)[0]?.name ?? 'IA'}
          onQuote={() => { setDemoPlan(CUSTOM_PLAN); scrollToDemo(); }}
        />
      </Box>

      <Box sx={{ textAlign: 'center', mt: 5 }}>
        <Button href="#comparar-planes" sx={pillBtn('outline')}>
          Comparar planes ↓
        </Button>
      </Box>

      <PlanComparison plans={plans} />
    </Section>
  );
};

/* =============================================================================
 * FAQ — editorial Q&A
 * ========================================================================== */
const FAQ: React.FC = () => {
  return (
    <Section bg={CREAM} id="faq">
      <Reveal>
        <SectionLabel idx="§ 05">Preguntas</SectionLabel>
        <Typography
          sx={{
            fontFamily: DISPLAY,
            fontSize: { xs: '2.2rem', md: '3.4rem' },
            lineHeight: 1.02,
            letterSpacing: '-0.02em',
            maxWidth: 800,
            mb: { xs: 5, md: 7 },
          }}
        >
          Lo que <Box component="em" sx={{ fontStyle: 'italic' }}>más nos preguntan.</Box>
        </Typography>
      </Reveal>

      <Box sx={{ maxWidth: 860 }}>
        {FAQS.map((f, i) => (
          <Reveal key={i}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                bgcolor: 'transparent',
                borderTop: `1px solid ${RULE_HARD}`,
                borderBottom: i === FAQS.length - 1 ? `1px solid ${RULE_HARD}` : 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: INK }} />}
                sx={{
                  px: 0,
                  py: 1,
                  '& .MuiAccordionSummary-content': { my: 2, alignItems: 'baseline', gap: 2 },
                }}
              >
                <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.16em', color: 'rgba(10,10,10,0.5)', flexShrink: 0, width: 40 }}>
                  {String(i + 1).padStart(2, '0')}
                </Typography>
                <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '1.3rem', md: '1.6rem' }, lineHeight: 1.2 }}>
                  {f.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pb: 3, pl: { xs: 0, md: 8 } }}>
                <Typography sx={{ fontFamily: SANS, fontSize: '1rem', lineHeight: 1.7, color: 'rgba(10,10,10,0.75)' }}>
                  {f.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Reveal>
        ))}
      </Box>
    </Section>
  );
};

/* =============================================================================
 * FINAL CTA — big yellow slab
 * ========================================================================== */
const FinalCTA: React.FC = () => {
  return (
    <Box component="section" id="cta" sx={{ bgcolor: YELLOW, color: INK, py: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden', borderTop: `1px solid ${INK}`, borderBottom: `1px solid ${INK}` }}>
      {/* Decorative slashes */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.12,
          backgroundImage: `repeating-linear-gradient(135deg, ${INK} 0 1px, transparent 1px 36px)`,
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Reveal>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={5}>
            <Box sx={{ maxWidth: 780 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.78rem', letterSpacing: '0.18em', mb: 3 }}>
                § FIN · EMPEZÁ HOY
              </Typography>
              <Typography
                sx={{
                  fontFamily: DISPLAY,
                  fontSize: { xs: '3rem', sm: '4.2rem', md: '6rem' },
                  lineHeight: 0.92,
                  letterSpacing: '-0.025em',
                  fontWeight: 400,
                }}
              >
                Dale. <Box component="em" sx={{ fontStyle: 'italic' }}>Arrancamos.</Box>
              </Typography>
              <Typography sx={{ fontFamily: SANS, fontSize: { xs: '1rem', md: '1.18rem' }, mt: 3, maxWidth: 540, color: 'rgba(10,10,10,0.82)' }}>
                Creá tu cuenta en dos minutos, conectá tus canales, y dejá de perseguir unidades que no existen.
              </Typography>
            </Box>

            <Stack spacing={2} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button
                component={RouterLink}
                to="/register"
                sx={{ ...pillBtn('solid'), px: 4, py: 2, fontSize: '1rem' }}
                endIcon={<ArrowOutward sx={{ fontSize: 18 }} />}
              >
                Empezar gratis
              </Button>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.74rem', letterSpacing: '0.14em', opacity: 0.7 }}>
                SIN TARJETA · SIN COMPROMISO
              </Typography>
            </Stack>
          </Stack>
        </Reveal>
      </Container>
    </Box>
  );
};

/* =============================================================================
 * FOOTER — terminal / receipt
 * ========================================================================== */
const Footer: React.FC = () => {
  return (
    <Box component="footer" sx={{ bgcolor: INK, color: CREAM_SOFT, py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <PartnersStrip />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: { xs: 4, md: 6 } }}>
          <Box>
            <Typography sx={{ fontFamily: DISPLAY, fontSize: '2rem', lineHeight: 1, mb: 2 }}>
              Dash<Box component="em" sx={{ fontStyle: 'italic', color: YELLOW }}>Flow</Box>
            </Typography>
            <Typography sx={{ fontFamily: SANS, fontSize: '0.9rem', color: 'rgba(255,255,255,0.62)', maxWidth: 360, mb: 3 }}>
              Gestión de stock, ventas y caja para comercios que venden en el mostrador,
              MercadoLibre y Tienda Nube. Hecho en Argentina.
            </Typography>

            <Box sx={{ fontFamily: MONO, fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.9 }}>
              <Box>▸ RUNTIME · 24/7 ONLINE</Box>
              <Box>▸ REGIÓN · ARGENTINA</Box>
              <Box>▸ SOPORTE · ES (HUMANO)</Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', mb: 1.2 }}>
                Encontranos en:
              </Typography>
              <Box
                component="a"
                href="https://apps.apple.com/app/id6776364740"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Descargar DashFlow en el App Store"
                sx={{ display: 'inline-block', lineHeight: 0 }}
              >
                <Box component="svg" viewBox="0 0 120 40" role="img" aria-label="Download on the App Store" sx={{ width: 132, height: 44, display: 'block' }}>
                  <rect x="0.5" y="0.5" width="119" height="39" rx="6.5" fill="#000" stroke="rgba(255,255,255,0.3)" />
                  <g transform="translate(9.5,7.7) scale(0.62)" fill="#fff">
                    <path d="M17.05 12.54c-.03-2.79 2.28-4.13 2.38-4.2-1.3-1.9-3.32-2.16-4.04-2.19-1.72-.17-3.36 1.01-4.23 1.01-.87 0-2.22-.99-3.65-.96-1.88.03-3.61 1.09-4.58 2.77-1.95 3.39-.5 8.41 1.4 11.16.93 1.35 2.04 2.86 3.49 2.81 1.4-.06 1.93-.9 3.62-.9 1.69 0 2.17.9 3.65.87 1.51-.03 2.46-1.37 3.38-2.73 1.06-1.56 1.5-3.08 1.52-3.16-.03-.01-2.92-1.12-2.95-4.45z" />
                    <path d="M14.69 4.35c.77-.93 1.29-2.22 1.15-3.51-1.11.04-2.46.74-3.25 1.67-.71.83-1.33 2.15-1.16 3.42 1.24.1 2.5-.63 3.26-1.58z" />
                  </g>
                  <text x="34" y="15.5" fill="#fff" fontFamily="-apple-system,Helvetica Neue,Arial,sans-serif" fontSize="6.5">Download on the</text>
                  <text x="33.5" y="30.5" fill="#fff" fontFamily="-apple-system,Helvetica Neue,Arial,sans-serif" fontSize="15" fontWeight="600">App Store</text>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box>
            <FooterTitle>Producto</FooterTitle>
            <FooterLink href="#features">Funcionalidades</FooterLink>
            <FooterLink href="#how">Cómo funciona</FooterLink>
            <FooterLink as={RouterLink} to="/precios">Planes y precios</FooterLink>
            <FooterLink href="#faq">Preguntas</FooterLink>
            <FooterLink as={RouterLink} to="/ayuda">Centro de ayuda</FooterLink>
          </Box>

          <Box>
            <FooterTitle>Canales</FooterTitle>
            <FooterLink href="#">MercadoLibre</FooterLink>
            <FooterLink href="#">Tienda Nube</FooterLink>
            <FooterLink href="#">Agente IA de WhatsApp</FooterLink>
          </Box>

          <Box>
            <FooterTitle>Cuenta</FooterTitle>
            <FooterLink as={RouterLink} to="/login">Ingresar</FooterLink>
            <FooterLink as={RouterLink} to="/register">Crear cuenta</FooterLink>
            <FooterLink as={RouterLink} to="/terminos">Términos y Condiciones</FooterLink>
            <FooterLink as={RouterLink} to="/privacidad">Privacidad</FooterLink>
          </Box>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mt: { xs: 6, md: 8 }, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.45)' }}>
            © {new Date().getFullYear()} · DASHFLOW · TODOS LOS DERECHOS RESERVADOS
          </Typography>
          <Stack direction="row" spacing={2}>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>v2.4.0</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>·</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>status: all systems go</Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

const FooterTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', mb: 2 }}>
    {children}
  </Typography>
);

type FooterLinkProps = {
  children: React.ReactNode;
  href?: string;
  to?: string;
  as?: React.ElementType;
};
const FooterLink: React.FC<FooterLinkProps> = ({ children, href, to, as }) => {
  const Component: React.ElementType = as || 'a';
  const extra = as ? { to } : { href };
  return (
    <Box
      component={Component}
      {...extra}
      sx={{
        display: 'block',
        fontFamily: SANS,
        fontSize: '0.92rem',
        color: 'rgba(255,255,255,0.82)',
        textDecoration: 'none',
        mb: 1,
        transition: 'color 0.2s',
        '&:hover': { color: YELLOW },
      }}
    >
      {children}
    </Box>
  );
};

/* =============================================================================
 * SESSION TRACKER — portado del session-tracker de TurnosPro.
 * Manda PageView + un resumen de interacción de la sesión (scroll máximo,
 * secciones vistas, clicks) a /api/tracking/event. El backend filtra bots,
 * persiste el evento y avisa por WhatsApp el resumen de cada sesión real.
 * ========================================================================== */
const TRACKING_API_BASE = process.env.REACT_APP_API_URL || '/api';

let trackerArmed = false;

function initLandingTracker() {
  // Evita doble init (StrictMode en dev / re-mounts del SPA).
  if (trackerArmed) return;
  trackerArmed = true;

  // Bot detection — skip ALL tracking for crawlers/headless browsers.
  // UA regex catches most bots; navigator.webdriver flags automation tools
  // (Selenium/Puppeteer/Playwright); missing navigator.languages is a
  // common headless Chrome signal.
  const isBot = (() => {
    try {
      const ua = navigator.userAgent || '';
      if (/bot|crawler|spider|crawling|headless|puppeteer|selenium|playwright|lighthouse|phantomjs|slurp|bingpreview|chrome-lighthouse|google page speed|gtmetrix|pingdom|monitor/i.test(ua)) return true;
      if ((navigator as any).webdriver === true) return true;
      if (!navigator.languages || navigator.languages.length === 0) return true;
      return false;
    } catch {
      return false;
    }
  })();
  if (isBot) return;

  // PageView: sid por sesión + persistencia de UTMs/fbclid de la query.
  try {
    const p = new URLSearchParams(window.location.search);
    const utms: Record<string, string> = {};
    const utmSource = p.get('utm_source');
    if (utmSource) { utms.utmSource = utmSource; sessionStorage.setItem('utm_source', utmSource); }
    const utmMedium = p.get('utm_medium');
    if (utmMedium) { utms.utmMedium = utmMedium; sessionStorage.setItem('utm_medium', utmMedium); }
    const utmCampaign = p.get('utm_campaign');
    if (utmCampaign) { utms.utmCampaign = utmCampaign; sessionStorage.setItem('utm_campaign', utmCampaign); }
    const fbclid = p.get('fbclid');
    if (fbclid) sessionStorage.setItem('fbclid', fbclid);
    let sid = sessionStorage.getItem('_track_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('_track_sid', sid);
    }
    fetch(`${TRACKING_API_BASE}/tracking/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType: 'PageView',
        url: window.location.href,
        device: window.innerWidth < 768 ? 'mobile' : 'desktop',
        referrer: document.referrer || undefined,
        sessionId: sid,
        fbclid: fbclid || sessionStorage.getItem('fbclid') || undefined,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        pageTitle: document.title,
        ...utms,
      }),
    }).catch(() => {
      /* el tracking nunca rompe la landing */
    });
  } catch {
    /* noop */
  }

  // Session interaction tracking
  try {
    const sess = {
      start: Date.now(),
      maxScroll: 0,
      clicks: [] as string[],
      sections: {} as Record<string, 1>,
    };
    let sessSent = false;
    window.addEventListener(
      'scroll',
      () => {
        const pct = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
        if (pct > sess.maxScroll) sess.maxScroll = pct;
        document.querySelectorAll('section[id]').forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0) sess.sections[el.id] = 1;
        });
      },
      { passive: true }
    );
    document.addEventListener('click', (e) => {
      const el = (e.target as Element | null)?.closest?.('button, a, .MuiAccordionSummary-root');
      if (!el) return;
      const text = (el.textContent || '').trim();
      const label = el.matches('button')
        ? `CTA: ${text.slice(0, 30)}`
        : el.matches('a')
          ? `Link: ${text.slice(0, 25)}`
          : `FAQ: ${text.slice(0, 30)}`;
      if (label && sess.clicks.indexOf(label) === -1) sess.clicks.push(label);
    });
    const sendExit = () => {
      if (sessSent) return;
      sessSent = true;
      const dur = Math.round((Date.now() - sess.start) / 1000);
      if (dur < 3) return;
      const sects = Object.keys(sess.sections).join(', ') || 'Solo hero';
      const clicks = sess.clicks.length > 0 ? sess.clicks.slice(0, 5).join(' | ') : 'Ninguno';
      const summary = `${dur}s | Scroll ${sess.maxScroll}% | Vio: ${sects} | Clicks: ${clicks}`;
      const sid = sessionStorage.getItem('_track_sid') || '';
      navigator.sendBeacon(
        `${TRACKING_API_BASE}/tracking/event`,
        new Blob(
          [
            JSON.stringify({
              eventType: 'SessionExit',
              url: window.location.href,
              name: summary,
              device: window.innerWidth < 768 ? 'mobile' : 'desktop',
              sessionId: sid,
              utmSource: sessionStorage.getItem('utm_source') || undefined,
              utmMedium: sessionStorage.getItem('utm_medium') || undefined,
              utmCampaign: sessionStorage.getItem('utm_campaign') || undefined,
              referrer: document.referrer || undefined,
            }),
          ],
          { type: 'application/json' }
        )
      );
    };
    window.addEventListener('beforeunload', sendExit);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') sendExit();
    });
  } catch {
    /* noop */
  }
}

/* =============================================================================
 * EN USO · captura real + fotos de personas (cards #22 y #33 del Trello)
 * La captura es del listado de ventas de una cuenta real, anonimizada (clientes,
 * vendedores y empresa ficticios; montos reales). Las fotos son de Unsplash (licencia
 * libre para uso comercial); créditos en public/media/CREDITOS.txt.
 * ========================================================================== */
const PEOPLE = [
  { src: '/media/persona-1-v1.webp', alt: 'Dueña de un local de ropa, de brazos cruzados detrás del mostrador', caption: 'Para la que atiende el mostrador y también vende online.' },
  { src: '/media/persona-2-v1.webp', alt: 'Dos socios sonriendo detrás del mostrador de su comercio', caption: 'Para los que llevan el negocio juntos y quieren ver los mismos números.' },
  { src: '/media/persona-3-v1.webp', alt: 'Vendedora con una tablet entre percheros de ropa en su tienda', caption: 'Para la que revisa el stock entre percheros, desde la tablet.' },
];

const InUse: React.FC = () => (
  <Section bg={CREAM} id="en-uso">
    <Reveal>
      <SectionLabel idx="§ 02·b">Así se ve por dentro</SectionLabel>
      <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '2.2rem', md: '3.4rem' }, lineHeight: 1.02, letterSpacing: '-0.01em', mb: { xs: 4, md: 6 }, maxWidth: 820 }}>
        Tu comercio, <Box component="em" sx={{ fontStyle: 'italic' }}>con los números a la vista.</Box>
      </Typography>
    </Reveal>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.55fr 1fr' }, gap: { xs: 3, md: 4 }, alignItems: 'stretch' }}>
      <Reveal>
        <Box component="figure" sx={{ m: 0, border: `1px solid ${INK}`, bgcolor: CREAM_SOFT, boxShadow: `6px 6px 0 ${YELLOW}` }}>
          <Box component="img" src="/media/momento-ventas-v1.webp" loading="lazy" alt="Listado de ventas de DashFlow con lo facturado, lo cobrado y lo pendiente del mes" sx={{ display: 'block', width: '100%', height: 'auto', borderBottom: `1px solid ${INK}` }} />
          <Box component="figcaption" sx={{ px: 2.5, py: 2, fontFamily: SANS, fontSize: '0.98rem' }}>
            Cada venta con lo facturado, lo cobrado y lo pendiente, mes por mes.
            <Box component="span" sx={{ display: 'block', fontFamily: MONO, fontSize: '0.66rem', letterSpacing: '0.14em', opacity: 0.55, mt: 0.8 }}>
              CAPTURA REAL · DATOS DE CLIENTES ANONIMIZADOS
            </Box>
          </Box>
        </Box>
      </Reveal>
      <Reveal>
        <Box component="figure" sx={{ m: 0, height: '100%', minHeight: { xs: 360, md: 0 }, position: 'relative', border: `1px solid ${INK}`, overflow: 'hidden' }}>
          <Box component="img" src={PEOPLE[0].src} alt={PEOPLE[0].alt} loading="lazy" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <Box component="figcaption" sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 2.5, background: `linear-gradient(180deg, transparent, ${INK}d9)`, color: CREAM_SOFT, fontFamily: DISPLAY, fontSize: '1.45rem', lineHeight: 1.15 }}>
            {PEOPLE[0].caption}
          </Box>
        </Box>
      </Reveal>
    </Box>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 3, md: 4 }, mt: { xs: 3, md: 4 } }}>
      {PEOPLE.slice(1).map((p) => (
        <Reveal key={p.src}>
          <Box component="figure" sx={{ m: 0, position: 'relative', aspectRatio: '3 / 2', border: `1px solid ${INK}`, overflow: 'hidden' }}>
            <Box component="img" src={p.src} alt={p.alt} loading="lazy" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <Box component="figcaption" sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 2.5, background: `linear-gradient(180deg, transparent, ${INK}d9)`, color: CREAM_SOFT, fontFamily: DISPLAY, fontSize: '1.35rem', lineHeight: 1.15 }}>
              {p.caption}
            </Box>
          </Box>
        </Reveal>
      ))}
    </Box>
  </Section>
);

/* =============================================================================
 * POPUP DE SALIDA "¿Antes de irte?" (card #22)
 * Solo con intención real de irse (ver watchExitIntent), nunca si tocó el formulario.
 * Una vez por sesión, nunca si ya pidió la demo, y solo en la home
 * pública (el mismo bundle sirve la app y los subdominios de cada empresa).
 * ========================================================================== */
/**
 * Intención real de irse (ajuste del 02/11/2026: antes salía "random", con un timer de 45 s en mobile).
 *  - Nunca en los primeros 10 s en la página.
 *  - Escritorio (puntero fino): el cursor sale de la ventana por arriba (pestañas, barra, cerrar) y
 *    solo si ya scrolleó al menos 30% del alto del viewport (o sea, ya leyó algo).
 *  - Táctil (pointer: coarse): sin timer. Si ya llegó al 40% del alto scrolleable y después sube
 *    rápido (≥ 60% del viewport en ≤ 600 ms), el gesto típico antes de ir a la barra o volver atrás.
 * Llama a onExit una sola vez y devuelve la función que saca los listeners.
 */
function watchExitIntent(onExit: () => void): () => void {
  const MIN_MS = 10_000;
  const start = Date.now();
  let maxY = window.scrollY;
  let fired = false;
  const samples: { y: number; t: number }[] = [];
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const fire = () => {
    if (fired || Date.now() - start < MIN_MS) return;
    fired = true;
    onExit();
  };
  const onScroll = () => {
    const y = window.scrollY;
    const t = Date.now();
    if (y > maxY) maxY = y;
    if (!coarse) return;
    samples.push({ y, t });
    while (samples.length && t - samples[0].t > 600) samples.shift();
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0 || maxY < scrollable * 0.4) return;
    const peak = Math.max(...samples.map((s) => s.y));
    if (peak - y >= window.innerHeight * 0.6) fire();
  };
  const onMouseOut = (e: MouseEvent) => {
    if (e.relatedTarget || e.clientY > 0) return;
    if (maxY < window.innerHeight * 0.3) return;
    fire();
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  if (!coarse) document.addEventListener('mouseout', onMouseOut);
  return () => {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onMouseOut);
  };
}

const EXIT_SHOWN_KEY = 'df_exit_shown';
const PUBLIC_HOSTS = ['dashflow.com.ar', 'www.dashflow.com.ar'];

const ExitIntent: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState('');
  const dialogRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!PUBLIC_HOSTS.includes(window.location.hostname) || window.location.pathname !== '/') return;
    const canShow = () => !sessionFlag(EXIT_SHOWN_KEY) && !sessionFlag(DEMO_SENT_KEY);
    const show = () => {
      if (!canShow()) return;
      setSessionFlag(EXIT_SHOWN_KEY);
      setOpen(true);
    };
    // Solo con intención real de irse; nunca si tocó un formulario de demo.
    return watchExitIntent(() => { if (!sessionFlag(DEMO_TOUCHED_KEY)) show(); });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => (document.getElementById('salida-name') as HTMLInputElement | null)?.focus(), 50);
    return () => { document.removeEventListener('keydown', onKey); window.clearTimeout(t); };
  }, [open]);

  if (!open) return null;
  return (
    <Box
      onClick={(e: React.MouseEvent) => { if (e.target === e.currentTarget) setOpen(false); }}
      sx={{ position: 'fixed', inset: 0, zIndex: 1400, bgcolor: 'rgba(10,10,10,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
    >
      <Box
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="salida-title"
        sx={{ position: 'relative', width: '100%', maxWidth: 520, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', bgcolor: CREAM_SOFT, color: INK, border: `1px solid ${INK}`, boxShadow: `6px 6px 0 ${YELLOW}`, p: { xs: 3, md: 4 } }}
      >
        <IconButton aria-label="Cerrar" onClick={() => setOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, color: INK }}>
          <CloseIcon />
        </IconButton>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', letterSpacing: '0.2em', opacity: 0.7, mb: 1.5 }}>DEMO GRATIS</Typography>
        <Typography id="salida-title" sx={{ fontFamily: DISPLAY, fontSize: { xs: '2.1rem', md: '2.5rem' }, lineHeight: 1.02, mb: 1.2 }}>
          ¿Antes de <Box component="em" sx={{ fontStyle: 'italic' }}>irte?</Box>
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: '1rem', color: 'rgba(10,10,10,0.7)', mb: 3 }}>
          Te llamamos dentro de los próximos 10 minutos.
        </Typography>
        <DemoLeadForm
          idPrefix="salida"
          pageSuffix="#salida"
          variant="callback"
          planOptions={[...PLANS.map((pl) => pl.name), CUSTOM_PLAN, UNDECIDED_PLAN]}
          plan={plan}
          onPlanChange={setPlan}
        />
      </Box>
    </Box>
  );
};

/* =============================================================================
 * PAGE
 * ========================================================================== */
const LandingPage: React.FC = () => {
  useEffect(() => {
    initLandingTracker();
  }, []);

  return (
    <Box sx={{ bgcolor: CREAM, color: INK, fontFamily: SANS, overflowX: 'hidden' }}>
      <Nav />
      <Hero />
      <Ticker />
      <Integrations />
      <Problem />
      <Features />
      <InUse />
      <HowItWorks />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
      <ExitIntent />
    </Box>
  );
};

export default LandingPage;
